import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Mic, MicOff, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const AIExpenseInput = ({ onParsed, eventId }) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const handleParse = async () => {
    if (!text.trim()) {
      toast.error('Please enter some text');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/expenses/parse', {
        text: text,
        event_id: eventId
      });
      onParsed(response.data);
      setText(''); // Clear input after successful parse
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse expense');
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setRecording(true);
      toast.info('Recording... Speak your expense details');
    } catch (error) {
      toast.error('Failed to start recording. Please check microphone permissions.');
      console.error('Recording error:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setLoading(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const formData = new FormData();
        formData.append('audio_data', base64Audio);
        
        const response = await api.post('/expenses/voice-transcribe', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.transcribed_text) {
          setText(response.data.transcribed_text);
          toast.success(`✨ Transcribed: "${response.data.transcribed_text}"`);
        }
        onParsed(response.data);
      };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transcribe audio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Example: Flight for Sam, Indigo, 5000 rupees&#10;Or: Hotel booking for revival meeting, Taj, $250&#10;Or: Food expenses for team, 3000 INR"
          rows={4}
          className="resize-none"
          data-testid="ai-expense-input"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          type="button"
          onClick={handleParse}
          disabled={loading || !text.trim()}
          data-testid="parse-expense-button"
          className="flex-1"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Parse with AI
            </>
          )}
        </Button>

        <Button
          type="button"
          variant={recording ? 'destructive' : 'outline'}
          onClick={recording ? stopRecording : startRecording}
          disabled={loading}
          data-testid="voice-record-button"
          className="flex-1 sm:flex-initial"
        >
          {recording ? (
            <>
              <MicOff className="h-4 w-4 mr-2" />
              Stop Recording
            </>
          ) : (
            <>
              <Mic className="h-4 w-4 mr-2" />
              Use Voice
            </>
          )}
        </Button>
      </div>

      <div className="text-xs text-muted-foreground space-y-1">
        <p>✨ <strong>AI Tip:</strong> Just describe your expense naturally!</p>
        <p>Examples: "Flight ticket for John, Indigo, 8500 rupees" or "Hotel stay at Marriott, $200"</p>
      </div>
    </div>
  );
};

export default AIExpenseInput;
